import { inject, Injectable, signal } from "@angular/core";

import { DateTime } from "luxon";

import { map, Observable, tap } from "rxjs";

import {
  getContractualMonth,
  transformTimestampToLastUpdated,
} from "@shared/functions";
import {
  ActivityRecordResponse,
  ContractualMonth,
  ParsedSequence,
  RequestState,
} from "@shared/models";
import { ActivityRecordService, GlobalService } from "@shared/services";
import { LocalStorage } from "@shared/enums";

interface DateRangeObject {
  rangeStart: string;
  rangeEnd: string;
  monthCalled: string;
}

@Injectable({
  providedIn: "root",
})
export class MySequencesService {
  activityRecordService = inject(ActivityRecordService);
  globalService = inject(GlobalService);

  lastUpdated = signal<DateTime | null>(null);

  /**
   * Gets the sequences for the month based on an offest from the last month called.
   * If there wasn't a month called previously, gets the sequences for the current month.
   * If successful, sets the last month called in local storage.
   * @param {number} offset - The offset of the month to get sequences for based on the last month called.
   * @param {boolean} force - Whether to force a refresh of the sequences.
   * @returns {Observable<ParsedSequence[]>} Array of {@link ParsedSequence} for the month as an Observable.
   */
  getMonthSequences(
    offset: number,
    force: boolean,
  ): Observable<ParsedSequence[]> {
    const { rangeStart, monthCalled } = this.getDateRanges(offset);
    let forceRefresh = false;
    if (force) {
      forceRefresh = true;
    }

    const handleResponse = (
      response: RequestState<ActivityRecordResponse>,
    ): ParsedSequence[] => {
      if (response.isLoading) {
        return [];
      } else {
        if (response.response) {
          const timestamp = DateTime.fromISO(response.response.timestamp);
          this.lastUpdated.set(timestamp);
          return this.activityRecordService.parseActivityRecord(
            response.response,
          );
        } else {
          return [];
        }
      }
    };
    return this.activityRecordService
      .getActivityRecord(
        rangeStart,
        forceRefresh,
        this.globalService.emulatedUser(),
      )
      .pipe(
        tap(() =>
          localStorage.setItem(LocalStorage.LastMonthCalled, monthCalled),
        ),
        map((response) => handleResponse(response)),
      );
  }

  /**
   * Gets the label for the month and year based on an offset from the last month called.
   * @param {number} offset - The offset in months for the last month called.
   * @returns {string} The label for the month and year.
   */
  getMonthLabel(offset: number): string {
    const lastMonthCalled = localStorage.getItem(LocalStorage.LastMonthCalled);
    if (!lastMonthCalled) {
      return `${getContractualMonth({ offset: 0 }).monthLong} ${DateTime.local().year}`;
    } else {
      let month = parseInt(lastMonthCalled.slice(-2)) + offset;
      let year = parseInt(lastMonthCalled.slice(0, 4));
      if (month === 13) {
        month = 1;
        year += 1;
      } else if (month === 0) {
        month = 12;
        year -= 1;
      }
      return `${getContractualMonth({ month: month }).monthLong} ${year}`;
    }
  }

  private getDateRanges(offset: number): DateRangeObject {
    // Get the last month called from local storage
    const lastMonthCalled = localStorage.getItem(LocalStorage.LastMonthCalled);
    let contractualMonth: ContractualMonth;
    let year = DateTime.local().year;

    if (!lastMonthCalled) {
      // If there is no last month called, get the current month
      contractualMonth = getContractualMonth({ offset: 0 });
    } else {
      // If there is a last month called, get the month based on the offset
      let month = parseInt(lastMonthCalled.slice(-2)) + offset;
      year = parseInt(lastMonthCalled.slice(0, 4));

      if (month === 13) {
        // Handle the case where the month is December
        month = 1;
        year += 1;
      } else if (month === 0) {
        // Handle the case where the month is January
        year -= 1;
        month = 12;
      }
      contractualMonth = getContractualMonth({ month: month });
    }
    return {
      rangeStart: DateTime.local()
        .set({
          year,
          month: parseInt(contractualMonth.start.month),
          day: parseInt(contractualMonth.start.day),
        })
        .toISODate(),

      rangeEnd: DateTime.local()
        .set({
          year,
          month: parseInt(contractualMonth.end.month),
          day: parseInt(contractualMonth.end.day),
        })
        .toISODate(),

      monthCalled: `${year}${contractualMonth.asInt}`,
    };
  }
}
